import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import type { SimWorld, GlobalCardStat, DailyCardSnapshot } from '../../simulation/types/SimWorld';
import { isGroupModeEnabled } from '../../simulation/types/SimWorld';
import type { SimNPC } from '../../simulation/types/SimNPC';
import type { GroupId, GroupConfig } from '../../simulation/types/Group';

interface CardStatsPanelProps {
  world: SimWorld;
  onNPCClick?: (npc: SimNPC) => void;
  isMobile?: boolean;
}

type SortKey = 'name' | 'attribute' | 'gamesUsed' | 'winsWithCard' | 'winRate';
type SortOrder = 'asc' | 'desc';

// 仮想スクロール設定
const ROW_HEIGHT = 40; // 各行の高さ（px）
const BUFFER_COUNT = 10; // 上下に余分に描画する行数

/**
 * カード統計パネル
 * Day別履歴付きでカードの使用回数・勝率を表示
 */
export const CardStatsPanel: React.FC<CardStatsPanelProps> = ({ world, onNPCClick, isMobile = false }) => {
  const [startDay, setStartDay] = useState<number>(1);
  const [endDay, setEndDay] = useState<number>(world.day);
  const [sortKey, setSortKey] = useState<SortKey>('winRate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [filterAttribute, setFilterAttribute] = useState<string>('all');
  const [filterCardType, setFilterCardType] = useState<string>('all');
  const [filterGroup, setFilterGroup] = useState<GroupId | 'all'>('all');
  const [minGames, setMinGames] = useState<number>(5);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(300);
  const containerRef = useRef<HTMLDivElement>(null);

  // グループモードの状態
  const groupModeActive = isGroupModeEnabled(world);
  const groups: GroupConfig[] = world.groupModeConfig?.groups || [];

  // 範囲内の統計を計算（endDayスナップショット - (startDay-1)スナップショット）
  const currentSnapshot = useMemo(() => {
    // スナップショットを day プロパティで検索するヘルパー
    const findSnapshotByDay = (day: number) => {
      return world.dailySnapshots.find((s) => s.day === day);
    };

    // endDayのデータを取得
    let endSnapshot: { stats: Map<string, GlobalCardStat>; totalGames: number };
    if (endDay >= world.day || world.dailySnapshots.length === 0) {
      endSnapshot = {
        stats: world.globalCardStats,
        totalGames: world.totalGamesPlayed,
      };
    } else {
      const snap = findSnapshotByDay(endDay);
      endSnapshot = snap ? { stats: snap.stats, totalGames: snap.totalGames } : {
        stats: world.globalCardStats,
        totalGames: world.totalGamesPlayed,
      };
    }

    // startDay-1のデータを取得（startDay=1なら0日目=データなし）
    if (startDay <= 1) {
      // Day 1から開始なので、endDayのデータをそのまま使用
      return {
        day: endDay,
        stats: endSnapshot.stats,
        totalGames: endSnapshot.totalGames,
      };
    }

    // startDay-1のスナップショットを取得
    const startSnapshot = findSnapshotByDay(startDay - 1);
    if (!startSnapshot) {
      return {
        day: endDay,
        stats: endSnapshot.stats,
        totalGames: endSnapshot.totalGames,
      };
    }

    // 差分を計算
    const diffStats = new Map<string, GlobalCardStat>();
    for (const [cardId, endStat] of endSnapshot.stats.entries()) {
      const startStat = startSnapshot.stats.get(cardId);
      if (startStat) {
        // 差分を計算
        const gamesUsed = endStat.gamesUsed - startStat.gamesUsed;
        const winsWithCard = endStat.winsWithCard - startStat.winsWithCard;
        if (gamesUsed > 0) {
          diffStats.set(cardId, {
            cardId: endStat.cardId,
            cardName: endStat.cardName,
            attribute: endStat.attribute,
            cardType: endStat.cardType || 'monster',
            gamesUsed,
            winsWithCard,
          });
        }
      } else {
        // startSnapshotに存在しない場合はそのまま
        if (endStat.gamesUsed > 0) {
          diffStats.set(cardId, endStat);
        }
      }
    }

    return {
      day: endDay,
      stats: diffStats,
      totalGames: endSnapshot.totalGames - startSnapshot.totalGames,
    };
  }, [startDay, endDay, world.day, world.globalCardStats, world.totalGamesPlayed, world.dailySnapshots]);

  // グループ別カード統計（グループフィルター時に使用）
  const groupFilteredStats = useMemo(() => {
    if (!groupModeActive || filterGroup === 'all') {
      return null; // グローバル統計を使用
    }

    // グループ内のNPCを取得
    const groupNpcs = world.npcs.filter(npc => npc.group === filterGroup);

    // NPCのcardStatsを集計
    const aggregatedStats = new Map<string, GlobalCardStat>();
    let totalGames = 0;

    for (const npc of groupNpcs) {
      if (!npc.cardStats) continue;

      const npcTotalGames = npc.wins + npc.losses;
      totalGames += npcTotalGames;

      for (const [cardId, stat] of npc.cardStats.entries()) {
        const existing = aggregatedStats.get(cardId);
        // カード情報をデッキから取得
        const card = npc.deck.find(c => c.id === cardId);
        if (existing) {
          existing.gamesUsed += stat.gamesWithCard || 0;
          existing.winsWithCard += stat.winsWithCard || 0;
        } else if (card) {
          aggregatedStats.set(cardId, {
            cardId,
            cardName: card.name,
            attribute: card.attribute || 'なし',
            cardType: card.type || 'monster',
            gamesUsed: stat.gamesWithCard || 0,
            winsWithCard: stat.winsWithCard || 0,
          });
        }
      }
    }

    return {
      day: world.day,
      stats: aggregatedStats,
      totalGames,
    };
  }, [world.npcs, world.day, filterGroup, groupModeActive]);

  // 実際に使用する統計（グループフィルター時はグループ統計、それ以外はグローバル統計）
  const effectiveStats = groupFilteredStats || currentSnapshot;

  // カードデータをソート・フィルタリング
  const sortedCards = useMemo(() => {
    const cards = Array.from(effectiveStats.stats.values())
      .filter((stat) => {
        // 属性フィルタ
        if (filterAttribute !== 'all' && stat.attribute !== filterAttribute) {
          return false;
        }
        // カード種別フィルタ
        if (filterCardType !== 'all') {
          // cardTypeが未設定の場合はmonsterとして扱う（後方互換性）
          const cardType = stat.cardType || 'monster';
          if (cardType !== filterCardType) {
            return false;
          }
        }
        // 最低使用回数フィルタ
        if (stat.gamesUsed < minGames) {
          return false;
        }
        return true;
      })
      .map((stat) => ({
        ...stat,
        winRate: stat.gamesUsed > 0 ? stat.winsWithCard / stat.gamesUsed : 0,
      }));

    // ソート
    cards.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (sortKey) {
        case 'name':
          aVal = a.cardName;
          bVal = b.cardName;
          break;
        case 'attribute':
          aVal = a.attribute;
          bVal = b.attribute;
          break;
        case 'gamesUsed':
          aVal = a.gamesUsed;
          bVal = b.gamesUsed;
          break;
        case 'winsWithCard':
          aVal = a.winsWithCard;
          bVal = b.winsWithCard;
          break;
        case 'winRate':
        default:
          aVal = a.winRate;
          bVal = b.winRate;
          break;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortOrder === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });

    return cards;
  }, [effectiveStats.stats, filterAttribute, filterCardType, minGames, sortKey, sortOrder]);

  // 利用可能な属性リスト
  const availableAttributes = useMemo(() => {
    const attrs = new Set<string>();
    for (const stat of effectiveStats.stats.values()) {
      attrs.add(stat.attribute);
    }
    return Array.from(attrs).sort();
  }, [effectiveStats.stats]);

  // 選択中のカードを持つNPCリスト（グループフィルタ対応）
  const npcsWithSelectedCard = useMemo(() => {
    if (!selectedCardId) return [];
    let npcs = world.npcs.filter((npc) =>
      npc.deck.some((card) => card.id === selectedCardId)
    );
    // グループフィルタが有効な場合、そのグループのNPCのみ表示
    if (groupModeActive && filterGroup !== 'all') {
      npcs = npcs.filter(npc => npc.group === filterGroup);
    }
    return npcs.sort((a, b) => {
      // 生存者優先、その後名前順
      if (a.isAlive !== b.isAlive) return a.isAlive ? -1 : 1;
      return a.name.localeCompare(b.name, 'ja');
    });
  }, [selectedCardId, world.npcs, groupModeActive, filterGroup]);

  // 選択中のカード情報
  const selectedCardInfo = useMemo(() => {
    if (!selectedCardId) return null;
    return effectiveStats.stats.get(selectedCardId) || null;
  }, [selectedCardId, effectiveStats.stats]);

  // コンテナサイズの監視
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateHeight = () => {
      setContainerHeight(container.clientHeight);
    };
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // スクロールハンドラ
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setScrollTop(target.scrollTop);
  }, []);

  // 仮想スクロール: 表示範囲の計算
  const totalHeight = sortedCards.length * ROW_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_COUNT);
  const visibleCount = Math.ceil(containerHeight / ROW_HEIGHT) + BUFFER_COUNT * 2;
  const endIndex = Math.min(sortedCards.length, startIndex + visibleCount);
  const visibleCards = sortedCards.slice(startIndex, endIndex);
  const offsetY = startIndex * ROW_HEIGHT;

  // ソートヘッダークリックハンドラ
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  // 属性の色を取得
  const getAttributeColor = (attr: string): string => {
    switch (attr) {
      case '炎': return '#ff6b6b';
      case '水': return '#6b9eff';
      case '光': return '#ffd700';
      case '闇': return '#9d4ce6';
      case '原始': return '#6bff6b';
      case '未来': return '#00bfff';
      case 'なし': return '#888';
      default: return '#aaa';
    }
  };

  // 勝率に応じた色を取得
  const getWinRateColor = (winRate: number): string => {
    if (winRate >= 0.6) return '#4ade80'; // Green
    if (winRate >= 0.5) return '#60a5fa'; // Blue
    if (winRate >= 0.4) return '#fbbf24'; // Yellow
    return '#ef4444'; // Red
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>🃏 カード統計</h3>

      {/* コントロールパネル */}
      <div style={styles.controls}>
        {/* Day 範囲スライダー */}
        <div style={styles.controlGroup}>
          <label style={styles.label}>
            Day: {startDay} ～ {endDay} / {world.day}
          </label>
          <div style={styles.rangeSliders}>
            <input
              type="range"
              min={1}
              max={Math.max(1, endDay)}
              value={startDay}
              onChange={(e) => {
                const newStart = parseInt(e.target.value);
                setStartDay(Math.min(newStart, endDay));
              }}
              style={styles.slider}
              title="開始日"
            />
            <input
              type="range"
              min={Math.max(1, startDay)}
              max={world.day}
              value={endDay}
              onChange={(e) => {
                const newEnd = parseInt(e.target.value);
                setEndDay(Math.max(newEnd, startDay));
              }}
              style={styles.slider}
              title="終了日"
            />
          </div>
        </div>

        {/* 属性フィルタ */}
        <div style={styles.controlGroup}>
          <label style={styles.label}>属性:</label>
          <select
            value={filterAttribute}
            onChange={(e) => setFilterAttribute(e.target.value)}
            style={styles.select}
          >
            <option value="all">すべて</option>
            {availableAttributes.map((attr) => (
              <option key={attr} value={attr}>
                {attr}
              </option>
            ))}
          </select>
        </div>

        {/* カード種別フィルタ */}
        <div style={styles.controlGroup}>
          <label style={styles.label}>種別:</label>
          <select
            value={filterCardType}
            onChange={(e) => setFilterCardType(e.target.value)}
            style={styles.select}
          >
            <option value="all">すべて</option>
            <option value="monster">モンスター</option>
            <option value="magic">魔法</option>
            <option value="field">フィールド</option>
            <option value="phasecard">フェイズ</option>
          </select>
        </div>

        {/* 最低使用回数フィルタ */}
        <div style={styles.controlGroup}>
          <label style={styles.label}>最低使用回数:</label>
          <select
            value={minGames}
            onChange={(e) => setMinGames(parseInt(e.target.value))}
            style={styles.select}
          >
            <option value={1}>1回以上</option>
            <option value={5}>5回以上</option>
            <option value={10}>10回以上</option>
            <option value={20}>20回以上</option>
            <option value={50}>50回以上</option>
          </select>
        </div>

        {/* グループフィルタ（グループモード有効時のみ表示） */}
        {groupModeActive && (
          <div style={styles.controlGroup}>
            <label style={styles.label}>グループ:</label>
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value as GroupId | 'all')}
              style={styles.select}
            >
              <option value="all">全体</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* サマリー */}
      <div style={styles.summary}>
        <span>総デュエル数: {effectiveStats.totalGames}</span>
        <span>表示カード数: {sortedCards.length}</span>
        <span>登録カード数: {effectiveStats.stats.size}</span>
        {groupModeActive && filterGroup !== 'all' && (
          <span style={{ color: groups.find(g => g.id === filterGroup)?.color || '#888' }}>
            📊 {groups.find(g => g.id === filterGroup)?.name}のみ
          </span>
        )}
      </div>

      {/* テーブル */}
      <div style={styles.tableWrapper}>
        {/* ヘッダー */}
        <div style={styles.tableHeader}>
          <span style={styles.thRank}>#</span>
          <SortableHeader
            label="カード名"
            sortKey="name"
            currentSortKey={sortKey}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          <SortableHeader
            label="属性"
            sortKey="attribute"
            currentSortKey={sortKey}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          <SortableHeader
            label="使用回数"
            sortKey="gamesUsed"
            currentSortKey={sortKey}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          <SortableHeader
            label="勝利回数"
            sortKey="winsWithCard"
            currentSortKey={sortKey}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          <SortableHeader
            label="勝率"
            sortKey="winRate"
            currentSortKey={sortKey}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </div>

        {/* 仮想スクロール対応ボディ */}
        <div
          ref={containerRef}
          style={styles.tableContainer}
          onScroll={handleScroll}
        >
          {sortedCards.length === 0 ? (
            <div style={styles.emptyMessage}>
              条件に一致するカードがありません
            </div>
          ) : (
            <div style={{ height: totalHeight, position: 'relative' }}>
              <div style={{ transform: `translateY(${offsetY}px)` }}>
                {visibleCards.map((card, index) => {
                  const actualIndex = startIndex + index;
                  return (
                    <div
                      key={card.cardId}
                      style={{
                        ...styles.tableRow,
                        height: ROW_HEIGHT,
                        backgroundColor: actualIndex % 2 === 0 ? '#16162a' : '#1a1a30',
                      }}
                    >
                      <span style={styles.tdRank}>{actualIndex + 1}</span>
                      <span
                        style={styles.tdCardName}
                        onClick={() => setSelectedCardId(card.cardId)}
                        title="クリックでこのカードを持つNPCを表示"
                      >
                        {card.cardName}
                      </span>
                      <span style={{ ...styles.tdAttribute, color: getAttributeColor(card.attribute) }}>
                        {card.attribute}
                      </span>
                      <span style={styles.tdNumeric}>{card.gamesUsed}</span>
                      <span style={styles.tdNumeric}>{card.winsWithCard}</span>
                      <span style={styles.tdWinRate}>
                        <div style={styles.winRateCell}>
                          <div
                            style={{
                              ...styles.winRateBar,
                              width: `${card.winRate * 100}%`,
                              backgroundColor: getWinRateColor(card.winRate),
                            }}
                          />
                          <span style={{ ...styles.winRateText, color: getWinRateColor(card.winRate) }}>
                            {(card.winRate * 100).toFixed(1)}%
                          </span>
                        </div>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* カード所持NPC一覧パネル */}
      {selectedCardId && selectedCardInfo && (
        <div style={styles.npcListPanel}>
          <div style={styles.npcListHeader}>
            <h4 style={styles.npcListTitle}>
              <span style={{ color: getAttributeColor(selectedCardInfo.attribute) }}>
                {selectedCardInfo.cardName}
              </span>
              を持つNPC ({npcsWithSelectedCard.length}人)
            </h4>
            <button
              style={styles.closeButton}
              onClick={() => setSelectedCardId(null)}
            >
              ✕
            </button>
          </div>
          <div style={styles.npcListContent}>
            {npcsWithSelectedCard.length === 0 ? (
              <div style={styles.emptyMessage}>このカードを持つNPCはいません</div>
            ) : (
              npcsWithSelectedCard.map((npc) => {
                const cardCount = npc.deck.filter((c) => c.id === selectedCardId).length;
                const totalBattles = npc.wins + npc.losses;
                const winRate = totalBattles > 0 ? ((npc.wins / totalBattles) * 100).toFixed(1) : '-';
                return (
                  <div
                    key={npc.id}
                    style={{
                      ...styles.npcListItem,
                      opacity: npc.isAlive ? 1 : 0.6,
                      cursor: onNPCClick ? 'pointer' : 'default',
                    }}
                    onClick={() => onNPCClick?.(npc)}
                  >
                    <span style={styles.npcStatus}>{npc.isAlive ? '🟢' : '💀'}</span>
                    <span style={styles.npcName}>{npc.name}</span>
                    <span style={styles.npcCardCount}>×{cardCount}</span>
                    <span style={styles.npcGeneration}>第{npc.generation || 1}世代</span>
                    <span
                      style={{
                        ...styles.npcWinRate,
                        color: winRate === '-' ? '#666' : parseFloat(winRate) >= 50 ? '#4ade80' : '#ef4444',
                      }}
                    >
                      {winRate === '-' ? '-' : `${winRate}%`}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * ソート可能なヘッダー
 */
interface SortableHeaderProps {
  label: string;
  sortKey: SortKey;
  currentSortKey: SortKey;
  sortOrder: SortOrder;
  onSort: (key: SortKey) => void;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  sortKey,
  currentSortKey,
  sortOrder,
  onSort,
}) => {
  const isActive = sortKey === currentSortKey;

  // カラム幅を取得
  const getWidth = () => {
    switch (sortKey) {
      case 'name': return '200px';
      case 'attribute': return '60px';
      case 'gamesUsed': return '80px';
      case 'winsWithCard': return '80px';
      case 'winRate': return '140px';
      default: return 'auto';
    }
  };

  return (
    <span
      style={{
        ...styles.th,
        ...styles.sortableHeader,
        width: getWidth(),
        minWidth: getWidth(),
      }}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive && (
        <span style={styles.sortIndicator}>
          {sortOrder === 'asc' ? ' ▲' : ' ▼'}
        </span>
      )}
    </span>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#1a1a2e',
    border: '2px solid #444',
    borderRadius: '8px',
    padding: '16px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  title: {
    margin: '0 0 16px 0',
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  controls: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '12px',
    padding: '12px',
    backgroundColor: '#16162a',
    borderRadius: '6px',
  },
  controlGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  label: {
    color: '#aaa',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  slider: {
    width: '120px',
    accentColor: '#60a5fa',
  },
  rangeSliders: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  select: {
    backgroundColor: '#2a2a3e',
    color: '#ddd',
    border: '1px solid #444',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '13px',
  },
  summary: {
    display: 'flex',
    gap: '24px',
    marginBottom: '12px',
    color: '#888',
    fontSize: '13px',
  },
  tableWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#16162a',
    borderRadius: '6px',
    border: '1px solid #2a2a3e',
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    backgroundColor: '#2a2a3e',
    borderBottom: '2px solid #444',
    flexShrink: 0,
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
  },
  tableRow: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #2a2a3e',
    fontSize: '13px',
    boxSizing: 'border-box',
  },
  th: {
    color: '#aaa',
    fontWeight: 'bold',
    padding: '10px 12px',
    textAlign: 'left',
    userSelect: 'none',
    fontSize: '13px',
  },
  thRank: {
    width: '40px',
    minWidth: '40px',
    color: '#aaa',
    fontWeight: 'bold',
    padding: '10px 12px',
    textAlign: 'center',
    fontSize: '13px',
  },
  sortableHeader: {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  sortIndicator: {
    color: '#60a5fa',
    fontSize: '11px',
  },
  tdRank: {
    width: '40px',
    minWidth: '40px',
    textAlign: 'center',
    color: '#888',
    padding: '0 12px',
  },
  tdCardName: {
    width: '200px',
    minWidth: '200px',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '0 12px',
    color: '#ddd',
    cursor: 'pointer',
  },
  tdAttribute: {
    width: '60px',
    minWidth: '60px',
    textAlign: 'center',
    padding: '0 12px',
  },
  tdNumeric: {
    width: '80px',
    minWidth: '80px',
    textAlign: 'right',
    fontFamily: 'monospace',
    padding: '0 12px',
    color: '#ddd',
  },
  tdWinRate: {
    width: '140px',
    minWidth: '140px',
    padding: '0 12px',
  },
  winRateCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '120px',
  },
  winRateBar: {
    height: '8px',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  winRateText: {
    minWidth: '50px',
    textAlign: 'right',
    fontWeight: 'bold',
    fontFamily: 'monospace',
  },
  emptyMessage: {
    color: '#666',
    fontSize: '14px',
    textAlign: 'center',
    padding: '40px 0',
  },
  clickableCardName: {
    cursor: 'pointer',
    transition: 'color 0.2s',
  },
  npcListPanel: {
    marginTop: '12px',
    backgroundColor: '#16162a',
    borderRadius: '6px',
    border: '1px solid #444',
    maxHeight: '200px',
    display: 'flex',
    flexDirection: 'column',
  },
  npcListHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    borderBottom: '1px solid #2a2a3e',
    flexShrink: 0,
  },
  npcListTitle: {
    margin: 0,
    fontSize: '14px',
    color: '#ddd',
    fontWeight: 'bold',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#888',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px 8px',
  },
  npcListContent: {
    flex: 1,
    overflow: 'auto',
    padding: '8px',
  },
  npcListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 8px',
    borderRadius: '4px',
    marginBottom: '4px',
    backgroundColor: '#1a1a30',
    fontSize: '13px',
    color: '#ddd',
    transition: 'background 0.2s',
  },
  npcStatus: {
    width: '20px',
    flexShrink: 0,
  },
  npcName: {
    flex: 1,
    minWidth: '80px',
    fontWeight: 500,
  },
  npcCardCount: {
    color: '#60a5fa',
    fontWeight: 'bold',
    minWidth: '30px',
  },
  npcGeneration: {
    color: '#888',
    fontSize: '12px',
    minWidth: '60px',
  },
  npcWinRate: {
    fontWeight: 'bold',
    minWidth: '45px',
    textAlign: 'right',
  },
};
